import { useEffect, useMemo, useState } from 'react';
import ServiceTypes, { types } from '../../service/ServiceTypes';
import AttToken from '../../helpers/attToken';
import MUIDataTable, { MUIDataTableOptions } from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { StyledDiv } from '../TableHelpers/TableStyle';
import { tableOptions } from '../TableHelpers/options';
import { RiDeleteBin5Line, RiPencilFill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ModalServiceType from './Modals/ModalServiceType';
import { AxiosError } from 'axios';
import { ThemeProvider } from '@mui/material/styles';
import { Button, Skeleton, Stack } from '@mui/material';
import { getMuiTheme } from "../TableHelpers/options";
import { IoInformationCircleOutline } from 'react-icons/io5';
import ServiceInfoModal from '../PlacesComp/Modals/ServiceInfoModal';
import { trackEventMatomo } from '../../helpers/matomo';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QueryFn from '../../helpers/QueryFn';

export type service = {
  nome: string,
  tipoId?: string,
  descricao?: string,
  dataCriacao?: number,
};

const queryFn = new QueryFn();

export default function ServicesManagement() {
  const serviceType = useMemo(() => new ServiceTypes(), []);
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation('translation');
  const [typeData, setTypeData] = useState<service | null>(null);
  const [services, setServices] = useState<service[]>([]);
  const [tipo, setTipo] = useState<types | null>(null);

  const { data, isLoading, isError } = queryFn.useFetch('GET_SERVICES',
    async () => {
      try {
        const token = await AttToken();
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const result = await serviceType.getAllServices(body);
          return result;
        }
      } catch (err) {
        console.error(err);
      }
    });

  useEffect(() => {
    if (data) {
      setServices(data);
    }
  }, [data]);

  const { mutate: removeType } = queryFn.useMutate('removeServiceType',
    async (value: string) => {
      toast.warn(`${t('Services.servicesManagement.removingServiceType')}`);
      try {
        const token = await AttToken();
        if (token) {
          const body = { ...token, empresa: token.userEmpresa, tipoId: value };
          await serviceType.deleteServiceType(body);
          toast.success(`${t('Services.servicesManagement.serviceRemoved')}`);
        }
      } catch (err) {
        console.error(err);
        toast.error(`${t('Services.servicesManagement.errorRemovingService')}`);
        return console.log(AxiosError);
      }
    });

  const columns = [
    {
      name: "nome",
      label: `${t('Services.servicesManagement.name')}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "descricao",
      label: `${t('Services.servicesManagement.description')}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "tipoId",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              className="button"
              onClick={() => {
                trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
                const selected = data?.filter((e: any) => e.tipoId === value)[0];
                setTypeData({
                  tipoId: selected.tipoId,
                  nome: selected.nome,
                  descricao: selected.descricao,
                  dataCriacao: selected.dataCriacao
                });
                // setIsEditing(true);
                setOpenModal(true);
              }}>
              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "tipoId",
      label: t('delete'),
      options: {
        customBodyRender: (value: string, _tableMeta: { rowIndex: number }) => (
          <Button
            endIcon={<RiDeleteBin5Line />}
            type='button'
            className="button is-danger"
            onClick={() => {
              trackEventMatomo('tela serviços', 'click', 'button', 'deleta serviço');
              removeType(value);
              setServices(services.filter(e => e.tipoId !== value));
            }}
          >
            {t('delete')}
          </Button>
        ),
        sort: false,
        filter: false
      }
    },
    {
      name: "tipoId",
      label: t('info'),
      options: {
        customBodyRender: (value: string) => (
          <button
            type='button'
            className="button is-small is-info"
            onClick={() => {
              trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
              const service = data?.find((e: any) => e.tipoId === value);
              setTipo(service || null);
            }}
          >
            <IoInformationCircleOutline size={30} />
          </button>
        ),
        sort: false,
        filter: false
      }
    }
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  if (isError) {
    return <span>Erro ao buscar dados</span>;
  }

  return (
    <>
      {openModal
        && <ModalServiceType
          open={openModal}
          setOpen={setOpenModal}
          typeData={typeData as service}
          setTypeData={setTypeData}
        />}
      {tipo && (
        <ServiceInfoModal
          tipo={tipo}
          setTipo={setTipo}
        />
      )}
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          disabled={isLoading}
          endIcon={<AddCircleIcon />}
          onClick={() => {
            trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
            setOpenModal(true);
          }}
          className='button is-success'>
          {`${t('notifications.ticket.criarTipoServico')}`}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          {isLoading
            ? <Skeleton variant='rounded' width={'100%'} height={400} />
            : <StyledDiv>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={`${t('Services.servicesManagement.kind')}`}
                  data={services}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </StyledDiv>
          }
        </Stack>
      </Stack>
    </>
  );
}